// 다국어 텍스트 타입
export interface MultiLanguageText {
  en: string;
  ko: string;
  ja: string;
  'zh-CN': string;
  'zh-TW': string;
}

// 대체 키워드 아이템
export interface AlternativeItem {
  text: MultiLanguageText;
  menuName?: string;
  franchiseId?: string;
  categoryId?: string;
}

// 메뉴 대체 추천 설정
export interface MenuAlternativeConfig {
  reason: MultiLanguageText;
  alternatives: AlternativeItem[];
  categoryConditions?: string[]; // 다중 카테고리 지원
}

// 브랜드 대체 추천 설정
export interface BrandAlternativeConfig {
  reason: MultiLanguageText;
  alternative: AlternativeItem; // 브랜드는 1:1 매핑
}

// 전체 대체 추천 설정
export interface SearchAlternatives {
  menu: Record<string, MenuAlternativeConfig>;
  brand: Record<string, BrandAlternativeConfig>;
}

// 메뉴 대체 추천 데이터
const menuAlternatives: Record<string, MenuAlternativeConfig> = {
  'Samgyeopsal': {
    reason: {
      en: 'Try these BBQ-style pork dishes.',
      ko: '다음 BBQ 스타일 돼지고기 요리를 시도해보세요.',
      ja: '代わりにこれらのBBQ風豚肉料理をどうぞ。',
      'zh-CN': '试试这些韩式烧烤风味的猪肉料理吧!',
      'zh-TW': '試試這些韓式燒烤風味的豬肉料理吧!'
    },
    alternatives: [
      {
        text: {
          en: 'Bulgogi (Marinated Beef)',
          ko: '불고기 (양념 소고기)',
          ja: 'プルコギ（味付け牛焼肉）',
          'zh-CN': '韩式烤牛肉 (Bulgogi)',
          'zh-TW': '韓式烤牛肉 (Bulgogi)'
        },
        menuName: 'Bulgogi',
        categoryId: 'JUGm4biv'
      },
      {
        text: {
          en: 'Bossam (Boiled Pork)',
          ko: '보쌈 (수육)',
          ja: 'ボッサム (ゆで豚包み)',
          'zh-CN': '韩式烫猪肉 (Bossam)',
          'zh-TW': '韓式燙豬肉 (Bossam)'
        },
        menuName: 'Bossam',
        categoryId: 'JUGm4biv'
      },
      {
        text: {
          en: 'Grilled Pork',
          ko: '돼지고기 구이',
          ja: '焼き豚',
          'zh-CN': '韩式烤猪肉',
          'zh-TW': '韓式烤豬肉'
        },
        menuName: 'Grilled Pork',
        categoryId: 'JUGm4biv'
      }
    ],
    categoryConditions: ['JUGm4biv', 'ljMA08ON']
  },

  'Bibimbap': {
    reason: {
      en: 'Try these healthy, mix-it-yourself bowls instead.',
      ko: '대신 이런 건강한 비빔 요리를 시도해보세요.',
      ja: '代わりにヘルシーで自分で混ぜるスタイルの丼を試してみて。',
      'zh-CN': '试试这些健康又能自己拌的饭吧!',
      'zh-TW': '試試這些健康又能自己拌的飯食吧!'
    },
    alternatives: [
      {
        text: {
          en: 'Rice Bowl',
          ko: '덮밥',
          ja: '丼',
          'zh-CN': '饭碗',
          'zh-TW': '蓋飯'
        },
        menuName: 'Rice Bowl'
      },
      {
        text: {
          en: 'Vegan Poke',
          ko: '비건 포케',
          ja: 'ヴィーガンポキ',
          'zh-CN': '轻食素食碗',
          'zh-TW': '純素波奇系列'
        },
        menuName: 'Vegan Poke'
      }
    ]
  },

  'Fried Chicken': {
    reason: {
      en: 'Try this crispy Korean street snack instead.',
      ko: '대신 바삭한 한국 길거리 간식을 시도해보세요.',
      ja: '代わりにサクサクの韓国屋台スナックをどうぞ。',
      'zh-CN': '试试这款酥脆的韩国街头小吃吧!',
      'zh-TW': '試試這款酥脆的韓國街頭小吃吧!'
    },
    alternatives: [
      {
        text: {
          en: 'Hot Dog',
          ko: '핫도그',
          ja: 'ホットドッグ',
          'zh-CN': '热狗',
          'zh-TW': '熱狗'
        },
        menuName: 'Hot Dog'
      }
    ]
  },

  'Tteokbokki': {
    reason: {
      en: 'Try these spicy Korean favorites with an addictive kick.',
      ko: '중독성 있는 매콤한 한국 인기 요리를 시도해보세요.',
      ja: 'ピリ辛でクセになる韓国の人気料理を試してみて。',
      'zh-CN': '试试这些又辣又上瘾的韩国人气美食吧!',
      'zh-TW': '試試這些又辣又讓人上癮的韓國人氣料理吧!'
    },
    alternatives: [
      {
        text: {
          en: 'Spicy Mixed Noodles',
          ko: '비빔면',
          ja: 'ピビン麺',
          'zh-CN': '辣拌面',
          'zh-TW': '辣拌麵'
        },
        menuName: 'Spicy Mixed Noodles'
      },
      {
        text: {
          en: 'Bibim Naengmyeon (Spicy Cold Buckwheat Noodles)',
          ko: '비빔냉면',
          ja: 'ビビン冷麺（辛口混ぜ麺）',
          'zh-CN': '辣拌冷面',
          'zh-TW': '辣拌冷麵'
        },
        menuName: 'Bibim Naengmyeon'
      }
    ]
  },

  'Bulgogi': {
    reason: {
      en: 'Try these BBQ-style pork dishes.',
      ko: '다음 BBQ 스타일 돼지고기 요리를 시도해보세요.',
      ja: '代わりにBBQ風の豚肉料理をどうぞ。',
      'zh-CN': '试试这些韩式烧烤风味的猪肉料理吧!',
      'zh-TW': '試試這些韓式燒烤風味的豬肉料理吧!'
    },
    alternatives: [
      {
        text: {
          en: 'Samgyeopsal (Grilled Pork Belly)',
          ko: '삼겹살',
          ja: 'サムギョプサル（豚バラ焼き肉）',
          'zh-CN': '五花肉烤肉',
          'zh-TW': '五花肉'
        },
        menuName: 'Samgyeopsal',
        categoryId: 'JUGm4biv'
      },
      {
        text: {
          en: 'Bossam (Boiled Pork)',
          ko: '보쌈 (수육)',
          ja: '韓式烤牛肉（醃製烤牛肉）',
          'zh-CN': '炭火烤豬肉',
          'zh-TW': '白婆婆包肉'
        },
        menuName: 'Bossam',
        categoryId: 'JUGm4biv'
      },
      {
        text: {
          en: 'Grilled Pork',
          ko: '돼지고기 구이',
          ja: '韩式烤牛肉（腌制烤牛肉）',
          'zh-CN': '炭火烤猪肉',
          'zh-TW': '烫猪肉（包饭）'
        },
        menuName: 'Grilled Pork',
        categoryId: 'JUGm4biv'
      }
    ],
    categoryConditions: ['JUGm4biv', 'ljMA08ON']
  },

  'Gimbap': {
    reason: {
      en: 'Try these light and satisfying Korean bites instead.',
      ko: '대신 가볍고 든든한 한국 음식을 시도해보세요.',
      ja: '軽くて満足感のある韓国グルメを試してみて。',
      'zh-CN': '试试这些轻盈又有满足感的韩式小吃吧!',
      'zh-TW': '試試這些輕盈又滿足的韓式小餐吧!'
    },
    alternatives: [
      {
        text: {
          en: 'Porridge',
          ko: '죽',
          ja: 'お粥',
          'zh-CN': '粥',
          'zh-TW': '粥'
        },
        menuName: 'Porridge'
      },
      {
        text: {
          en: 'Toast',
          ko: '토스트',
          ja: 'トースト',
          'zh-CN': '吐司',
          'zh-TW': '吐司'
        },
        menuName: 'Toast'
      }
    ]
  },

  'Jjajang': {
    reason: {
      en: 'Try these bold and satisfying Korean noodle dishes instead.',
      ko: '대신 진하고 든든한 한국 면 요리를 시도해보세요.',
      ja: '代わりにパンチのある満足感たっぷりの韓国麺料理をどうぞ。',
      'zh-CN': '试试这些够味又有满足感的韩式面食吧!',
      'zh-TW': '試試這些夠味又有飽足感的韓式麵食吧!'
    },
    alternatives: [
      {
        text: {
          en: 'Naengmyeon (Cold Buckwheat Noodles in Broth)',
          ko: '냉면',
          ja: '冷麺（スープ入り）',
          'zh-CN': '冷面（汤面）',
          'zh-TW': '冷麵（湯麵）'
        },
        menuName: 'Naengmyeon'
      },
      {
        text: {
          en: 'Bibim Naengmyeon (Spicy Cold Buckwheat Noodles)',
          ko: '비빔냉면',
          ja: 'ビビン冷麺（辛口混ぜ麺）',
          'zh-CN': '辣拌冷面',
          'zh-TW': '辣拌冷麵'
        },
        menuName: 'Bibim Naengmyeon'
      }
    ]
  },

  'Naengmyeon': {
    reason: {
      en: 'Try this savory Korean noodle favorite instead.',
      ko: '대신 감칠맛 나는 한국 면 요리를 시도해보세요.',
      ja: '代わりに旨みたっぷりの韓国麺料理をどうぞ。',
      'zh-CN': '试试这道咸香可口的韩式人气面食吧!',
      'zh-TW': '試試這道鹹香可口的韓式人氣麵食吧!'
    },
    alternatives: [
      {
        text: {
          en: 'Jjajangmyeon (Black Bean Noodles)',
          ko: '짜장면',
          ja: 'ジゾンジャジャン麺',
          'zh-CN': '至尊炸酱面',
          'zh-TW': '至尊炸醬麵'
        },
        menuName: 'Jjajang',
        categoryId: 'O0mj4M0y'
      },
      {
        text: {
          en: 'Spicy Mixed Noodles',
          ko: '비빔면',
          ja: 'ピビン麺',
          'zh-CN': '辣拌面',
          'zh-TW': '辣拌麵'
        },
        menuName: 'Spicy Mixed Noodles'
      }
    ]
  },

  'Sundubu Jjigae': {
    reason: {
      en: 'Warm up with these soothing Korean soups and porridges.',
      ko: '따뜻하고 편안한 한국 국물 요리와 죽으로 몸을 따뜻하게 하세요.',
      ja: '代わりに心まで温まる韓国のスープやお粥をどうぞ。',
      'zh-CN': '来碗温暖治愈的韩式汤品或粥吧!',
      'zh-TW': '來碗溫暖療癒的韓式湯品或粥吧!'
    },
    alternatives: [
      {
        text: {
          en: 'Galbitang (Beef Short Rib Soup)',
          ko: '갈비탕',
          ja: 'カルビタン（牛カルビスープ）',
          'zh-CN': '排骨汤（牛排骨汤）',
          'zh-TW': '排骨湯（牛排骨湯）'
        },
        menuName: 'Galbitang'
      },
      {
        text: {
          en: 'Seolleongtang (Ox Bone Soup)',
          ko: '설렁탕',
          ja: 'ソルロンタン（牛骨スープ）',
          'zh-CN': '雪浓汤（牛骨汤）',
          'zh-TW': '雪濃湯（牛骨湯）'
        },
        menuName: 'Seolleongtang'
      },
      {
        text: {
          en: 'Porridge',
          ko: '죽',
          ja: 'お粥',
          'zh-CN': '粥',
          'zh-TW': '粥'
        },
        menuName: 'Porridge'
      }
    ]
  },

  'Hot Dog': {
    reason: {
      en: 'Try these fun and flavorful Korean street bites instead.',
      ko: '대신 재미있고 맛있는 한국 길거리 음식을 시도해보세요.',
      ja: '代わりに楽しくて美味しい韓国の屋台グルメをどうぞ。',
      'zh-CN': '试试这些有趣又美味的韩国街头小吃吧!',
      'zh-TW': '試試這些有趣又美味的韓國街頭小吃吧!'
    },
    alternatives: [
      {
        text: {
          en: 'Tteokbokki',
          ko: '떡볶이',
          ja: 'トッポッキ',
          'zh-CN': '辣炒年糕',
          'zh-TW': '辣炒年糕'
        },
        menuName: 'Tteokbokki'
      },
      {
        text: {
          en: 'Toast',
          ko: '토스트',
          ja: 'トースト',
          'zh-CN': '吐司',
          'zh-TW': '吐司'
        },
        menuName: 'Toast'
      }
    ]
  },

  'Toast': {
    reason: {
      en: 'Try these bold and satisfying Korean street snacks instead.',
      ko: '대신 진하고 든든한 한국 길거리 간식을 시도해보세요.',
      ja: '代わりにパンチが効いて満足感のある韓国の屋台グルメをどうぞ。',
      'zh-CN': '试试这些够味又有满足感的韩国街头小吃吧!',
      'zh-TW': '試試這些夠味又飽足的韓國街頭小吃吧!'
    },
    alternatives: [
      {
        text: {
          en: 'Hot Dog',
          ko: '핫도그',
          ja: 'ホットドッグ',
          'zh-CN': '热狗',
          'zh-TW': '熱狗'
        },
        menuName: 'Hot Dog'
      },
      {
        text: {
          en: 'Tteokbokki',
          ko: '떡볶이',
          ja: 'トッポッキ',
          'zh-CN': '辣炒年糕',
          'zh-TW': '辣炒年糕'
        },
        menuName: 'Tteokbokki'
      }
    ]
  },

  'Galbitang': {
    reason: {
      en: 'Try these warm and gentle Korean comfort foods instead.',
      ko: '대신 따뜻하고 부드러운 한국 위안 음식을 시도해보세요.',
      ja: '代わりにやさしくて温まる韓国の癒しごはんをどうぞ。',
      'zh-CN': '试试这些温和又暖心的韩式治愈料理吧!',
      'zh-TW': '試試這些溫和又暖心的韓式療癒料理吧!'
    },
    alternatives: [
      {
        text: {
          en: 'Seolleongtang (Ox Bone Soup)',
          ko: '설렁탕',
          ja: 'ソルロンタン（牛骨スープ）',
          'zh-CN': '雪浓汤（牛骨汤）',
          'zh-TW': '雪濃湯（牛骨湯）'
        },
        menuName: 'Seolleongtang'
      },
      {
        text: {
          en: 'Porridge',
          ko: '죽',
          ja: 'お粥',
          'zh-CN': '粥',
          'zh-TW': '粥'
        },
        menuName: 'Porridge'
      }
    ]
  }
};

// 브랜드 대체 추천 데이터
const brandAlternatives: Record<string, BrandAlternativeConfig> = {
  'BBQ': {
    reason: {
      en: 'BHC brings the same ultra-crispy bite with its signature "KwakSak King" fried chicken.',
      ko: 'BHC의 시그니처 "꽈삭킹" 치킨으로 똑같이 바삭한 식감을 즐기세요.',
      ja: 'BHCの「クァクサッキング」は同じく超サクサク食感の自慢のフライドチキンです。',
      'zh-CN': 'BHC的招牌"KwakSak King"炸鸡同样酥脆可口，不容错过!',
      'zh-TW': 'BHC的招牌「KwakSak King」炸雞同樣酥脆爽口、不容錯過!'
    },
    alternative: {
      text: {
        en: 'BHC',
        ko: 'BHC',
        ja: 'BHC',
        'zh-CN': 'BHC',
        'zh-TW': 'BHC'
      },
      franchiseId: 'Jk5WeHw4'
    }
  },

  'BHC': {
    reason: {
      en: 'Nene Chicken offers the same bold flavors and fun sides like cheese balls and seasoned fries.',
      ko: 'Nene Chicken은 치즈볼과 시즈닝 감자튀김 같은 재미있는 사이드와 함께 똑같이 진한 맛을 제공합니다.',
      ja: 'Nene Chickenなら、チーズボールや味付きポテトなど、同じくらい楽しくてパンチのある味が楽しめます。',
      'zh-CN': '可以试试Nene Chicken，同样有芝士球、调味薯条等丰富又有趣的美味选择!',
      'zh-TW': '那就試試Nene Chicken，同樣有起司球和調味薯條等有趣又濃郁的美味!'
    },
    alternative: {
      text: {
        en: 'Nene Chicken',
        ko: '네네치킨',
        ja: 'Nene Chicken',
        'zh-CN': 'Nene Chicken',
        'zh-TW': 'Nene Chicken'
      },
      franchiseId: 'nene'
    }
  },

  'Kyochon Chicken': {
    reason: {
      en: 'Try Gikoba Chicken for bold, grilled soy-garlic flavor with a smoky twist.',
      ko: 'Gikoba Chicken의 진한 간장마늘 맛과 스모키한 풍미를 시도해보세요.',
      ja: 'Gikobaチキンの香ばしく焼き上げた醤油ガーリックチキンをお試しください。',
      'zh-CN': '试试 Gikoba Chicken，酱油蒜香浓郁，带有烟熏风味。',
      'zh-TW': '試試 Gikoba Chicken，醬油蒜香味濃郁，帶有炭烤風味。'
    },
    alternative: {
      text: {
        en: 'Gikoba Chicken',
        ko: '기꼬바치킨',
        ja: 'Gikoba Chicken',
        'zh-CN': 'Gikoba Chicken',
        'zh-TW': 'Gikoba Chicken'
      },
      franchiseId: 'Nf2QrEc2'
    }
  },

  'Nene Chicken': {
    reason: {
      en: 'BHC brings the same bold flavors and fun sides like cheese balls and seasoned fries.',
      ko: 'BHC 치킨도 치즈볼과 시즈닝 감자튀김 등 풍부한 사이드와 대담한 맛을 제공합니다.',
      ja: 'BHCチキンなら、チーズボールや味付きポテトなどの楽しいサイドと大胆な味付けが楽しめます。',
      'zh-CN': 'BHC Chicken 同样提供大胆口味和芝士球、调味薯条等丰富小吃。',
      'zh-TW': 'BHC Chicken 也有濃郁口味和起司球、調味薯條等豐富小點。'
    },
    alternative: {
      text: {
        en: 'BHC',
        ko: 'BHC',
        ja: 'BHC',
        'zh-CN': 'BHC',
        'zh-TW': 'BHC'
      },
      franchiseId: 'Pq1UkOh4'
    }
  },

  'Goobne Chicken': {
    reason: {
      en: 'Matdakko Chicken serves roasted-style chicken that\'s juicy, savory, and lighter than fried.',
      ko: 'Matdakko는 육즙이 풍부하고 고소하며 튀김보다 가벼운 로스트 스타일을 제공합니다.',
      ja: 'Matdakkoは、ジューシーで香ばしいローストスタイルを楽しめます。',
      'zh-CN': 'Matdakko 提供多汁香浓的烤鸡，比油炸更清爽。',
      'zh-TW': 'Matdakko 提供多汁又香濃的烤雞，比油炸更清爽。'
    },
    alternative: {
      text: {
        en: 'Matdakko',
        ko: '맛닭꼬',
        ja: 'Matdakko',
        'zh-CN': 'Matdakko',
        'zh-TW': 'Matdakko'
      },
      franchiseId: 'Zm7HtPk7'
    }
  }
};

// 전체 대체 추천 설정 export
export const searchAlternatives: SearchAlternatives = {
  menu: menuAlternatives,
  brand: brandAlternatives
}; 